import { pluralizeWithCount } from "@/lib/utils";
import { eqArrays, eqSets } from "../../shared/utils";
import {
  addCurrentPageEventListener,
  objectToQueryString,
} from "../../shared/utils";

export function filterMultiselect(args) {
  const {
    getFilters = () => null,
    itemsName,
    onChange,
    onSelect,
    treatAllAsNone,
    updateOptionsEndpoint,
    updateOptionsEvent,
    remoteSearchPath,
  } = args;

  const selectedIds = new Set(args.selectedIds);

  let originalValue, rootIds, originalItems;

  return {
    selectedIds,
    disabledIds: new Set(),
    additions: new Set(),
    deletions: new Set(),
    dynamicPrompt: null,
    isOpen: false,
    items: args.items,
    searchTermPresent: false,
    itemsUpdating: false,
    // Virtual scrolling properties
    itemHeight: 40, // Height of each item in pixels
    visibleItems: [],
    scrollTop: 0,
    containerHeight: 288, // max-h-72 = 18rem = 288px
    init() {
      originalItems = this.items;
      this.items = this.sortedItems();
      rootIds = new Set(this.items.map((s) => s.id));
      this.updateVisibleItems();
      this.setPrompt();
      if (updateOptionsEvent) {
        addCurrentPageEventListener(updateOptionsEvent, () => {
          this.updateOptions();
        });
      }
    },
    get selectedIdsArr() {
      return Array.from(this.selectedIds);
    },
    updateOptions() {
      this.items = [];
      this.itemsUpdating = true;

      const filtersQueryString = objectToQueryString(getFilters());

      // API call to endpoint that must return JSON with `options` array.
      //   {"options": [{"id": "one", "name": "one"}, {"id": "two", "name": "two"]}
      fetch(`${updateOptionsEndpoint}${"?" + filtersQueryString}`)
        .then((res) => res.json())
        .then((data) => {
          const stillSelected = new Set();

          originalItems = data.options;
          originalItems.forEach((option) => {
            if (this.selectedIds.has(option.id)) stillSelected.add(option.id);
          });

          rootIds = new Set(data.options.map((s) => s.id));

          this.selectedIds = stillSelected;
          this.items = this.sortedItems();
          this.updateVisibleItems();
          this.itemsUpdating = false;
          this.setPrompt();
        });
    },
    toggleDropdown() {
      if (this.isOpen) {
        return this.unfocus();
      }

      this.focus();
    },
    focus() {
      this.isOpen = true;

      originalValue = this.selectedIdsArr;

      const searchField = this.$root.querySelector(".x-searchInput");
      this.items = this.sortedItems();
      this.updateVisibleItems();
      this.$nextTick(() => {
        searchField.focus();
      });
    },
    unfocus() {
      if (!this.isOpen) return;

      this.isOpen = false;

      const currentValue = this.selectedIdsArr;
      const changed = !eqArrays(originalValue, currentValue);
      const allSelected = eqSets(this.selectedIds, rootIds);

      this.$nextTick(() => {
        if (this.$refs.scrollContainer) {
          this.$refs.scrollContainer.scrollTop = 0;
        }

        if (changed) {
          if (allSelected && treatAllAsNone) {
            onChange(this.$root, []);
          } else {
            onChange(this.$root, Array.from(currentValue));
          }
        }
      });
    },
    emitOnSelect() {
      if (!onSelect) return;

      const allSelected = eqSets(this.selectedIds, rootIds);
      const currentValue = this.selectedIdsArr;

      if (allSelected && treatAllAsNone) {
        onSelect(this.$root, []);
      } else {
        onSelect(this.$root, Array.from(currentValue));
      }
    },
    setPrompt() {
      if (eqSets(this.selectedIds, rootIds)) {
        this.dynamicPrompt = `All ${pluralizeWithCount(itemsName, null)}`;
      } else if (this.selectedIds.size > 0) {
        this.dynamicPrompt = pluralizeWithCount(
          itemsName,
          this.selectedIds.size,
        );
      } else {
        this.dynamicPrompt = null;
      }
    },
    selectItem(id) {
      this.selectedIds.add(id);
      this.emitOnSelect();
    },
    deselectItem(id) {
      this.selectedIds.delete(id);
      this.emitOnSelect();
    },
    isSelected(id) {
      return this.selectedIds.has(id);
    },
    disableId(id) {
      this.disabledIds.add(id);
    },
    undisableId(id) {
      this.disabledIds.delete(id);
    },
    isDisabled(id) {
      return this.disabledIds.has(id);
    },
    toggleSelectItem(id) {
      if (this.isDisabled(id)) return;

      if (this.isSelected(id)) {
        // Check if this was added during this session
        if (this.additions.has(id)) {
          // Cancel out the addition
          this.additions.delete(id);
        } else {
          // Must have been originally selected, mark as deleted
          this.deletions.add(id);
        }

        this.deselectItem(id);
      } else {
        // Check if this was deleted during this session
        if (this.deletions.has(id)) {
          // Cancel out the deletion
          this.deletions.delete(id);
        } else {
          // Must not have been originally selected, mark as added
          this.additions.add(id);
        }

        this.selectItem(id);
      }

      this.setPrompt();
    },
    selectAll() {
      this.items.forEach((item) => {
        if (!this.selectedIds.has(item.id)) {
          this.toggleSelectItem(item.id);
        }
      });
    },
    deselectAll() {
      const selectedIdsArray = Array.from(this.selectedIds);
      selectedIdsArray.forEach((id) => {
        this.toggleSelectItem(id);
      });

      this.disabledIds.clear();
    },
    loadState(state) {
      // Load the complete state (selectedIds, additions, deletions)
      this.selectedIds.clear();
      state.selectedIds.forEach((id) => this.selectedIds.add(id));

      this.additions.clear();
      state.additions.forEach((id) => this.additions.add(id));

      this.deletions.clear();
      state.deletions.forEach((id) => this.deletions.add(id));

      this.setPrompt();
      this.emitOnSelect();
    },
    get additionsArr() {
      return Array.from(this.additions);
    },
    get deletionsArr() {
      return Array.from(this.deletions);
    },
    search(e) {
      const searchTerm = e.target.value;

      this.searchTermPresent = searchTerm.length > 0;

      if (remoteSearchPath) {
        this.itemsUpdating = true;
        fetch(
          `${remoteSearchPath}?q=${searchTerm}&selected=${this.selectedIdsArr.join()}`,
        )
          .then((response) => response.json())
          .then((data) => {
            this.items = data;
            originalItems = this.items;
            this.items = this.sortedItems();
            this.updateVisibleItems();
            this.itemsUpdating = false;
          });
      } else {
        if (this.searchTermPresent) {
          this.items = originalItems.filter((item) => {
            return item.name.toLowerCase().includes(searchTerm.toLowerCase());
          });
          this.updateVisibleItems();
          return;
        }
        this.items = this.sortedItems();
        this.updateVisibleItems();
      }
    },
    sortedItems() {
      return originalItems.sort((a, b) => {
        const aSelected = this.isSelected(a.id) ? 1 : 0;
        const bSelected = this.isSelected(b.id) ? 1 : 0;
        return bSelected - aSelected;
      });
    },
    updateVisibleItems() {
      const startIndex = Math.floor(this.scrollTop / this.itemHeight);
      const endIndex = Math.min(
        startIndex + Math.ceil(this.containerHeight / this.itemHeight) + 1,
        this.items.length,
      );

      this.visibleItems = this.items
        .slice(startIndex, endIndex)
        .map((item, index) => ({
          ...item,
          virtualIndex: startIndex + index,
          style: `transform: translateY(${(startIndex + index) * this.itemHeight}px); height: ${this.itemHeight}px;`,
        }));
    },
    handleScroll(e) {
      this.scrollTop = e.target.scrollTop;
      this.updateVisibleItems();
    },
    get totalHeight() {
      return this.items.length * this.itemHeight;
    },
  };
}
